/**
 * Utilities for handling dates in Brazil timezone (UTC-3)
 */

/**
 * Formats a date to Brazil timezone
 * @param {string|Date} date - Date to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date in Brazil timezone
 */
export function FormatBrazilDate(date, options = {}) {
    if (!date) {
        return '';
    }

    const dateObj = typeof date === 'string' ? new Date(date) : date;

    const defaultOptions = {
        timeZone: 'America/Sao_Paulo',
        ...options,
    };

    return dateObj.toLocaleString('pt-BR', defaultOptions);
}

/**
 * Formats a date to Brazil timezone (date only)
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date (DD/MM/YYYY)
 */
export function FormatBrazilDateOnly(date) {
    return FormatBrazilDate(date, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
}

/**
 * Formats a date to Brazil timezone (date and time)
 * @param {string|Date} date - Date to format (UTC)
 * @returns {string} Formatted date and time (DD/MM/YYYY HH:mm:ss) in Brazil timezone
 */
export function FormatBrazilDateTime(date) {
    if (!date) {
        return '';
    }

    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Usar toLocaleString com timezone de Sao Paulo
    return dateObj.toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
}

/**
 * Formats a date to Brazil timezone (date and time without seconds)
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date and time (DD/MM/YYYY HH:mm)
 */
export function FormatBrazilDateTimeShort(date) {
    return FormatBrazilDate(date, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * Formats a period (from - to) in Brazil timezone
 * @param {string|Date} from - Start date (UTC)
 * @param {string|Date} to - End date (UTC)
 * @returns {string} Formatted period (DD/MM/YYYY - DD/MM/YYYY) in Brazil timezone
 */
export function FormatBrazilPeriod(from, to) {
    if (!from || !to) {
        return '';
    }
    return `${FormatBrazilDateOnly(from)} - ${FormatBrazilDateOnly(to)}`;
}

/**
 * Converts a Brazil date (YYYY-MM-DD) to UTC start of day
 * @param {string} dateStr - Date string in YYYY-MM-DD format (Brazil timezone)
 * @returns {string} ISO string in UTC (start of day in Brazil = 03:00 UTC)
 *
 * Example: "2025-10-12" (12/10 00:00 Brasilia) -> "2025-10-12T03:00:00.000Z" (12/10 03:00 UTC)
 */
function normalizeYmdParts(dateInput) {
    // Aceita string YYYY-MM-DD, Date, ISO ou timestamp
    if (dateInput instanceof Date && !Number.isNaN(dateInput.getTime())) {
        return [dateInput.getFullYear(), dateInput.getMonth() + 1, dateInput.getDate()];
    }

    const asString = typeof dateInput === 'string' ? dateInput : String(dateInput ?? '');

    // Remover parte de tempo se vier ISO
    const [datePart] = asString.split('T');
    const segments = datePart.split('-').map(Number);

    if (segments.length >= 3 && segments.every((n) => !Number.isNaN(n))) {
        const [year, month, day] = segments;
        return [year, month, day];
    }

    // Fallback: tentar criar Date
    const fallback = new Date(dateInput);
    if (!Number.isNaN(fallback.getTime())) {
        return [fallback.getFullYear(), fallback.getMonth() + 1, fallback.getDate()];
    }

    throw new Error(`Data invalida: ${dateInput}`);
}

export function BrazilDateToUTCStart(dateStr) {
    const [year, month, day] = normalizeYmdParts(dateStr);
    // Brasilia esta UTC-3, entao 00:00 em Brasilia = 03:00 UTC do mesmo dia
    const date = new Date(Date.UTC(year, month - 1, day, 3, 0, 0, 0));
    return date.toISOString();
}

/**
 * Converts a Brazil date (YYYY-MM-DD) to UTC end of day
 * @param {string} dateStr - Date string in YYYY-MM-DD format (Brazil timezone)
 * @returns {string} ISO string in UTC (end of day in Brazil = 02:59:59.999 UTC next day)
 *
 * Example: "2025-10-12" (12/10 23:59 Brasilia) -> "2025-10-13T02:59:59.999Z" (13/10 02:59 UTC)
 */
export function BrazilDateToUTCEnd(dateStr) {
    const [year, month, day] = normalizeYmdParts(dateStr);
    // Brasilia esta UTC-3, entao 23:59:59.999 em Brasilia = 02:59:59.999 UTC do dia seguinte
    const date = new Date(Date.UTC(year, month - 1, day + 1, 2, 59, 59, 999));
    return date.toISOString();
}
